// Boleto (Brasil) e Pix copia e cola: lê o código e tira o valor, o vencimento e quem recebe, sem internet e sem IA.
// Boleto bancário: linha digitável de 47 números ou código de barras de 44 (o vencimento vem do "fator de vencimento").
// Contas de consumo e tributos (arrecadação): 48 números começando com 8 (só o valor; o vencimento não vem no código).
import { addDays, today } from './date';

export type CodigoLido = {
  tipo: 'boleto' | 'arrecadacao' | 'pix';
  codigo: string;            // como deve ser colado no app do banco
  valor: number | null;
  vencimento: string | null; // AAAA-MM-DD
  banco?: string;            // código do banco (boleto)
  recebedor?: string;        // Pix: nome de quem recebe
};

const so = (s: string) => s.replace(/\D/g, '');
const diasEntre = (a: string, b: string) => Math.round((Date.parse(a) - Date.parse(b)) / 864e5);

/** Fator de vencimento: dias desde 07/10/1997. Em 22/02/2025 o fator chegou a 9999 e recomeçou em 1000.
 *  Escolhe a data (das duas possíveis) mais perto de hoje. */
export function dataDoFator(fator: number): string | null {
  if (!fator) return null;
  const antiga = addDays('1997-10-07', fator);
  const nova = fator >= 1000 ? addDays('2025-02-22', fator - 1000) : null;
  if (!nova) return antiga;
  const h = today();
  return Math.abs(diasEntre(nova, h)) <= Math.abs(diasEntre(antiga, h)) ? nova : antiga;
}

function mod10(num: string) {
  let soma = 0, peso = 2;
  for (let i = num.length - 1; i >= 0; i--) {
    let p = Number(num[i]) * peso;
    if (p > 9) p = Math.floor(p / 10) + (p % 10);
    soma += p; peso = peso === 2 ? 1 : 2;
  }
  const r = (10 - (soma % 10)) % 10;
  return r;
}

/** Converte a linha digitável do boleto bancário (47) no código de barras (44). */
function barrasDoBoleto(l: string) {
  return l.slice(0, 4) + l[32] + l.slice(33, 47) + l.slice(4, 9) + l.slice(10, 20) + l.slice(21, 31);
}

/** Pix copia e cola (padrão EMV): campos em "id(2) + tamanho(2) + valor". */
function lerPix(txt: string): CodigoLido | null {
  const s = txt.trim();
  if (!s.startsWith('000201')) return null;
  const campos: Record<string, string> = {};
  for (let i = 0; i + 4 <= s.length;) {
    const id = s.slice(i, i + 2), n = Number(s.slice(i + 2, i + 4));
    if (!isFinite(n)) break;
    campos[id] = s.slice(i + 4, i + 4 + n);
    i += 4 + n;
  }
  const valor = campos['54'] ? Number(campos['54']) : null;
  return { tipo: 'pix', codigo: s, valor: valor && isFinite(valor) ? valor : null, vencimento: null, recebedor: campos['59']?.trim() || undefined };
}

/** Lê o que a pessoa colou ou a IA achou: linha digitável, código de barras ou Pix copia e cola. */
export function lerCodigo(txt: string): CodigoLido | null {
  if (!txt) return null;
  const pix = lerPix(txt);
  if (pix) return pix;
  const d = so(txt);
  // arrecadação (luz, água, telefone, tributos): 48 números (linha) ou 44 (barras), começando com 8
  if (d[0] === '8' && (d.length === 48 || d.length === 44)) {
    const barras = d.length === 48 ? d.slice(0, 11) + d.slice(12, 23) + d.slice(24, 35) + d.slice(36, 47) : d;
    const efetivo = barras[2] === '6' || barras[2] === '8';
    const v = Number(barras.slice(4, 15)) / 100;
    return { tipo: 'arrecadacao', codigo: d, valor: efetivo && v > 0 ? v : null, vencimento: null };
  }
  // boleto bancário
  if (d.length === 47 || d.length === 44) {
    if (d.length === 47) {
      // confere os dígitos dos 3 primeiros campos (evita aceitar número digitado errado)
      const ok = mod10(d.slice(0, 9)) === Number(d[9]) && mod10(d.slice(10, 20)) === Number(d[20]) && mod10(d.slice(21, 31)) === Number(d[31]);
      if (!ok) return null;
    }
    const barras = d.length === 47 ? barrasDoBoleto(d) : d;
    const fator = Number(barras.slice(5, 9)), v = Number(barras.slice(9, 19)) / 100;
    return { tipo: 'boleto', codigo: d, valor: v > 0 ? v : null, vencimento: dataDoFator(fator), banco: barras.slice(0, 3) };
  }
  return null;
}

/** Linha digitável com os espaços de costume, para ler na tela. */
export function formatarCodigo(c: string) {
  const d = so(c);
  if (d.length === 47) return `${d.slice(0, 5)}.${d.slice(5, 10)} ${d.slice(10, 15)}.${d.slice(15, 21)} ${d.slice(21, 26)}.${d.slice(26, 32)} ${d[32]} ${d.slice(33)}`;
  if (d.length === 48) return `${d.slice(0, 12)} ${d.slice(12, 24)} ${d.slice(24, 36)} ${d.slice(36)}`;
  return c;
}
